import { useQueryClient, UseQueryResult } from "@tanstack/react-query";
import React from "react";
import { CustomOnboardingRedirectionParams, Page } from "../../activities/AppPage";
import { API } from "../../API";
import { isLoading, loading } from "../../Loading";
import { HRApplicationForm, HRSection } from "../../messages/HR";
import { STRINGS } from "../../strings";
import { AppState } from "../../system/AppState";
import { translate } from "../../translate";
import CountdownModal from "../CountdownModal";
import AppModal from "../Generic/AppModal/AppModal";
import {
  getSectionTitle,
  isReferenceSection,
  isSectionCompletedByQuestionGroups,
  isSectionReadyToSignByQuestionGroups,
  sendHrApplicationAnswer,
} from "../Onboarding/utils/onboardingUtils";
import useOnboardingOrientation from "./hooks/useOnboardingOrientation";
import useStage from "./hooks/useStage";
import OnboardingSectionScreen from "./OnboardingSectionScreen";

interface Props {
  appState: AppState;
  api: API;
  sections: HRSection[];
  section: HRSection;
  language: string;
  caregiverApplicationQuery: UseQueryResult<HRApplicationForm, unknown>;
  customDirectionParams: CustomOnboardingRedirectionParams | null;
  onClickGoBack: () => void;
  onRequestNavigate: (page: Page) => void;
  onRequestNavigateReplace: (page: Page) => void;
}

const OnboardingSectionActivity = (props: Props) => {
  const queryClient = useQueryClient();
  const orientation = useOnboardingOrientation({
    appState: props.appState,
    orientationBundleDueDateId:
      props.caregiverApplicationQuery.data?.orientationBundleDueDateId ?? loading(),
    onRequestNavigate: props.onRequestNavigate,
  });

  const handleClickGoBack = () => {
    queryClient.invalidateQueries(["caregiver-application"]);
    props.onClickGoBack();
  };

  const { state, actions, nextStageModal } = useStage({
    section: props.section,
    sections: props.sections,
    language: props.language,
    hasCompletedOrientation: orientation.isCompleted,
    caregiverApplicationQuery: props.caregiverApplicationQuery,
    customDirectionParams: props.customDirectionParams,
    onRequestNavigateReplace: props.onRequestNavigateReplace,
    onRequestStartWatchingOrientation: orientation.navigateToBundle,
    onClickGoBack: handleClickGoBack,
  });

  const isSectionCompleted = isSectionCompletedByQuestionGroups(state.questionGroups);

  const isSignatureDisabled = !isSectionReadyToSignByQuestionGroups(
    state.currentStage,
    state.questionGroups,
    state.questionGroupIdToSubsectionId
  );

  const caregiverState = props.appState.appCaregiverState;

  if (isLoading(caregiverState)) {
    return null;
  }

  return (
    <>
      <OnboardingSectionScreen
        title={getSectionTitle(props.section, props.language)}
        titleSecondary={state.title}
        questionGroups={state.questionGroups}
        currentStageNumber={state.currentStageNumber}
        percentage={state.percentage}
        totalStages={state.totalStages}
        currentStage={state.currentStage}
        isStageLoading={state.isLoading}
        language={props.language}
        emailVerificationTemplateId={
          props.caregiverApplicationQuery.data?.emailVerificationTemplateId ?? null
        }
        isSectionCompleted={isSectionCompleted}
        isSignaureDisabled={isSignatureDisabled}
        isReferenceSection={isReferenceSection(props.section)}
        shareUrl={state.shareUrl}
        isCompliant={state.isCompliant}
        answerMutationFn={(question) =>
          sendHrApplicationAnswer({
            api: props.api,
            caregiverId: caregiverState.caregiver.id,
            question: question,
          })
        }
        onRequestPreviousStage={actions.goToPreviousStage}
        onCompleteGroup={actions.handleAnswers}
        onClickGoBack={handleClickGoBack}
        onInvalidSubmit={nextStageModal.appModal.setModalAsError}
      />
      <AppModal {...nextStageModal.appModal.modalProps} />
      <CountdownModal
        title={nextStageModal.title}
        message={nextStageModal.message}
        initialCountdown={10}
        nextButtonText={translate(STRINGS.NEXT_SECTION)}
        closeButtonText={translate(STRINGS.BACK_TO_ONBOARDING)}
        isOpen={nextStageModal.isOpen}
        onCountdownEnd={nextStageModal.handleMoveToNextSection}
        onClickNext={nextStageModal.handleMoveToNextSection}
        onRequestClose={handleClickGoBack}
      />
    </>
  );
};

export default OnboardingSectionActivity;
